import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ImageIcon, ChevronRight, Search, X } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Button } from "@/components/ui/button"

type ImageRow = {
  id: number
  caption: string | null
  alt_text: string | null
  attachment_count: number
  tag_names: string[]
  has_file: boolean
  url: string | null
  created_at: string
}

type Props = {
  images: ImageRow[]
  total_count: number
  search: string | null
  missing_caption: boolean
}

export default function AdminImagesIndex({ images, total_count, search, missing_caption }: Props) {
  const [query, setQuery] = useState(search ?? "")

  function buildParams(overrides: Record<string, string | boolean> = {}) {
    const p: Record<string, string> = {}
    const q = overrides.search !== undefined ? String(overrides.search) : query.trim()
    const mc = overrides.missing_caption !== undefined ? overrides.missing_caption : missing_caption
    if (q) p.search = q
    if (mc) p.missing_caption = "1"
    return p
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get("/admin/images", buildParams(), { preserveState: false })
  }

  function clearSearch() {
    setQuery("")
    router.get("/admin/images", buildParams({ search: "" }), { preserveState: false })
  }

  function toggleMissingCaption() {
    router.get("/admin/images", buildParams({ missing_caption: !missing_caption }), { preserveState: false })
  }

  const isFiltering = !!search || missing_caption

  return (
    <>
      <Head title="Images">
        <meta name="description" content="Manage images in the admin area." />
        <meta property="og:title" content="Images" />
        <meta property="og:description" content="Manage images in the admin area." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Images</h1>
              <p className="mt-1">
                {isFiltering
                  ? `${total_count} ${total_count === 1 ? "result" : "results"}`
                  : `${total_count} ${total_count === 1 ? "image" : "images"} · showing ${images.length} most recent`}
              </p>
            </div>
            <Button asChild>
              <Link href="/admin/images/new">New image</Link>
            </Button>
          </div>

          <form onSubmit={handleSearch} className="mt-4 flex flex-col gap-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted pointer-events-none" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by caption or alt text…"
                  className="w-full rounded border border-hairline bg-page pl-9 pr-9 py-2 text-sm text-ink-body placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-current"
                />
                {query && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-body transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button type="submit" variant="secondary">Search</Button>
            </div>
            <label className="flex items-center gap-2 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={missing_caption}
                onChange={toggleMissingCaption}
                className="h-4 w-4 rounded border-hairline accent-current"
              />
              <span className="text-sm text-ink-muted">Missing caption only</span>
            </label>
          </form>
        </div>

        {images.length === 0 ? (
          <p className="mt-8 text-center text-sm text-ink-muted">
            {isFiltering ? "No images match your filters." : "No images yet."}
          </p>
        ) : (
          <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
            {images.map((image) => (
              <li key={image.id}>
                <Link
                  href={`/admin/images/${image.id}`}
                  className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface"
                >
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-surface">
                    {image.url ? (
                      <img
                        src={image.url}
                        alt={image.alt_text ?? ""}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <ImageIcon className="h-5 w-5 text-ink-muted" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink-display">
                      {image.caption || <span className="italic text-ink-muted">No caption</span>}
                    </div>
                    <div className="truncate text-xs text-ink-muted">
                      {image.alt_text && <span>{image.alt_text} · </span>}
                      {image.attachment_count} {image.attachment_count === 1 ? "attachment" : "attachments"}
                      {image.tag_names.length > 0 && ` · ${image.tag_names.join(", ")}`}
                      {!image.has_file && " · no file"}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </AdminShell>
    </>
  )
}
